/**
 * Vocabulaire des icônes de navigation du catalogue des capacités (UPT-2079 F2, rest §6.3) : données seulement, copiées dans
 * `capability-catalog.v1.json` par `pnpm catalog:build` (clés `iconAliases` et `iconKeywords`).
 *
 * - `ICON_ALIASES` : jeton d'icône lu dans le prototype (`data-icon`, classe `icon-*` / `lucide-*` / `fa-*`, `<use href>`) vers une
 *   clé du catalogue, après normalisation (minuscules, sans préfixe de bibliothèque).
 * - `ICON_KEYWORDS` : mots du libellé (sans accents, minuscules) vers une clé du catalogue ; l'ordre est significatif (le premier mot
 *   du libellé qui touche une ligne l'emporte, les lignes se lisent dans l'ordre). Un mot de 4 lettres ou plus touche un mot du
 *   libellé qui commence par lui (« projet » touche « projets », « fisc » touche « fiscaux ») ; un mot plus court doit être égal.
 */
export const ICON_ALIASES: Readonly<Record<string, string>> = {
  "grid": "Squares2X2Icon",
  "dashboard": "Squares2X2Icon",
  "layout": "Squares2X2Icon",
  "home": "HomeIcon",
  "house": "HomeIcon",
  "wallet": "WalletIcon",
  "wealth": "BuildingLibraryIcon",
  "bank": "BuildingLibraryIcon",
  "landmark": "BuildingLibraryIcon",
  "target": "FlagIcon",
  "flag": "FlagIcon",
  "goal": "FlagIcon",
  "trend": "ArrowTrendingUpIcon",
  "trending-up": "ArrowTrendingUpIcon",
  "sun": "SunIcon",
  "moon": "MoonIcon",
  "user": "UserIcon",
  "users": "UserGroupIcon",
  "tax": "ReceiptPercentIcon",
  "percent": "ReceiptPercentIcon",
  "receipt": "ReceiptPercentIcon",
  "book": "BookOpenIcon",
  "book-open": "BookOpenIcon",
  "settings": "Cog6ToothIcon",
  "cog": "Cog6ToothIcon",
  "gear": "Cog6ToothIcon",
  "chart": "ChartBarIcon",
  "bar-chart": "ChartBarIcon",
  "pie-chart": "ChartPieIcon",
  "calendar": "CalendarDaysIcon",
  "file": "DocumentIcon",
  "file-text": "DocumentTextIcon",
  "document": "DocumentTextIcon",
  "folder": "FolderIcon",
  "mail": "EnvelopeIcon",
  "envelope": "EnvelopeIcon",
  "bell": "BellIcon",
  "search": "MagnifyingGlassIcon",
  "star": "StarIcon",
  "heart": "HeartIcon",
  "lock": "LockClosedIcon",
  "key": "KeyIcon",
  "shield": "ShieldCheckIcon",
  "money": "BanknotesIcon",
  "cash": "BanknotesIcon",
  "banknote": "BanknotesIcon",
  "coins": "BanknotesIcon",
  "credit-card": "CreditCardIcon",
  "cart": "ShoppingCartIcon",
  "shopping-cart": "ShoppingCartIcon",
  "truck": "TruckIcon",
  "tool": "WrenchIcon",
  "tools": "WrenchIcon",
  "wrench": "WrenchIcon",
  "bolt": "BoltIcon",
  "zap": "BoltIcon",
  "clock": "ClockIcon",
  "map-pin": "MapPinIcon",
  "phone": "PhoneIcon",
  "message": "ChatBubbleLeftRightIcon",
  "chat": "ChatBubbleLeftRightIcon",
  "info": "InformationCircleIcon",
  "help": "QuestionMarkCircleIcon",
  "list": "ListBulletIcon",
  "table": "TableCellsIcon",
  "briefcase": "BriefcaseIcon",
  "building": "BuildingOffice2Icon",
  "store": "BuildingStorefrontIcon",
  "globe": "GlobeAltIcon",
  "image": "PhotoIcon",
  "camera": "CameraIcon",
  "tag": "TagIcon",
  "trophy": "TrophyIcon",
  "award": "TrophyIcon",
  "puzzle": "PuzzlePieceIcon",
  "gift": "GiftIcon",
  "graduation-cap": "AcademicCapIcon",
  "lightbulb": "LightBulbIcon",
  "server": "ServerIcon",
  "database": "CircleStackIcon",
  "cpu": "CpuChipIcon",
  "scale": "ScaleIcon",
  "fire": "FireIcon"
};

export const ICON_KEYWORDS: ReadonlyArray<{ icon: string; words: readonly string[] }> = [
  {
    "icon": "Squares2X2Icon",
    "words": [
      "ensemble",
      "tableau",
      "accueil",
      "dashboard",
      "overview",
      "synthese",
      "apercu",
      "cockpit"
    ]
  },
  {
    "icon": "WalletIcon",
    "words": [
      "budget",
      "portefeuille",
      "wallet",
      "depense",
      "charges"
    ]
  },
  {
    "icon": "BuildingLibraryIcon",
    "words": [
      "patrimoine",
      "banque",
      "bank",
      "fortune",
      "capital",
      "avoirs",
      "actifs"
    ]
  },
  {
    "icon": "FlagIcon",
    "words": [
      "projet",
      "objectif",
      "goal",
      "jalon"
    ]
  },
  {
    "icon": "ArrowTrendingUpIcon",
    "words": [
      "simul",
      "trend",
      "evolution",
      "croissance",
      "rendement",
      "performance",
      "progression",
      "prevision",
      "projection",
      "forecast"
    ]
  },
  {
    "icon": "SunIcon",
    "words": [
      "retraite",
      "retirement",
      "vacances"
    ]
  },
  {
    "icon": "UserIcon",
    "words": [
      "profil",
      "profile",
      "personne",
      "identite"
    ]
  },
  {
    "icon": "UserGroupIcon",
    "words": [
      "equipe",
      "team",
      "groupe",
      "membres",
      "famille",
      "foyer",
      "client"
    ]
  },
  {
    "icon": "UsersIcon",
    "words": [
      "utilisateur",
      "users",
      "personnel",
      "employe",
      "collaborateur",
      "salarie",
      "annuaire"
    ]
  },
  {
    "icon": "ReceiptPercentIcon",
    "words": [
      "fisc",
      "impot",
      "tax",
      "taxe",
      "tva",
      "deduction",
      "remise",
      "reduction",
      "promo"
    ]
  },
  {
    "icon": "BookOpenIcon",
    "words": [
      "comprendre",
      "ressource",
      "guide",
      "glossaire",
      "documentation",
      "apprendre",
      "aide"
    ]
  },
  {
    "icon": "AcademicCapIcon",
    "words": [
      "formation",
      "cours",
      "ecole",
      "apprentissage",
      "etudiant",
      "diplome",
      "quiz"
    ]
  },
  {
    "icon": "BanknotesIcon",
    "words": [
      "revenu",
      "epargne",
      "argent",
      "salaire",
      "paiement",
      "money",
      "income",
      "savings",
      "financ"
    ]
  },
  {
    "icon": "CreditCardIcon",
    "words": [
      "carte"
    ]
  },
  {
    "icon": "DocumentTextIcon",
    "words": [
      "document",
      "facture",
      "contrat",
      "rapport",
      "report",
      "devis"
    ]
  },
  {
    "icon": "CalendarDaysIcon",
    "words": [
      "calendrier",
      "agenda",
      "planning",
      "echeance",
      "rendez",
      "calendar",
      "schedule",
      "evenement"
    ]
  },
  {
    "icon": "CalendarIcon",
    "words": [
      "date",
      "jour"
    ]
  },
  {
    "icon": "ChartBarIcon",
    "words": [
      "statist",
      "analys",
      "analytic",
      "indicateur",
      "kpi",
      "mesure",
      "metrique",
      "chart"
    ]
  },
  {
    "icon": "ChartPieIcon",
    "words": [
      "repartition",
      "allocation",
      "camembert"
    ]
  },
  {
    "icon": "ChartBarSquareIcon",
    "words": [
      "bilan"
    ]
  },
  {
    "icon": "Cog6ToothIcon",
    "words": [
      "parametre",
      "reglage",
      "configuration",
      "setting",
      "admin",
      "preference"
    ]
  },
  {
    "icon": "BriefcaseIcon",
    "words": [
      "emploi",
      "carriere",
      "mission",
      "job",
      "travail"
    ]
  },
  {
    "icon": "FolderIcon",
    "words": [
      "dossier",
      "classeur",
      "archive",
      "repertoire"
    ]
  },
  {
    "icon": "ShieldCheckIcon",
    "words": [
      "securite",
      "assurance",
      "protection",
      "conformite",
      "compliance"
    ]
  },
  {
    "icon": "ShieldExclamationIcon",
    "words": [
      "risque",
      "alerte",
      "incident"
    ]
  },
  {
    "icon": "TruckIcon",
    "words": [
      "livraison",
      "transport",
      "expedition",
      "logistique",
      "vehicule"
    ]
  },
  {
    "icon": "ShoppingCartIcon",
    "words": [
      "achat",
      "commande",
      "panier",
      "vente",
      "order"
    ]
  },
  {
    "icon": "BuildingOffice2Icon",
    "words": [
      "entreprise",
      "societe",
      "organisation",
      "bureau",
      "company"
    ]
  },
  {
    "icon": "BuildingStorefrontIcon",
    "words": [
      "magasin",
      "boutique",
      "fournisseur",
      "catalogue",
      "shop"
    ]
  },
  {
    "icon": "HomeIcon",
    "words": [
      "home",
      "maison",
      "logement",
      "immobilier",
      "loyer"
    ]
  },
  {
    "icon": "EnvelopeIcon",
    "words": [
      "courrier",
      "email",
      "mail",
      "contact"
    ]
  },
  {
    "icon": "ChatBubbleLeftRightIcon",
    "words": [
      "message",
      "discussion",
      "chat",
      "commentaire",
      "echange"
    ]
  },
  {
    "icon": "ClockIcon",
    "words": [
      "historique",
      "heure",
      "temps",
      "horaire",
      "pointage",
      "duree"
    ]
  },
  {
    "icon": "BellIcon",
    "words": [
      "notification",
      "rappel"
    ]
  },
  {
    "icon": "LightBulbIcon",
    "words": [
      "idee",
      "astuce",
      "conseil",
      "innovation",
      "suggestion"
    ]
  },
  {
    "icon": "TrophyIcon",
    "words": [
      "classement",
      "ranking",
      "palmares",
      "recompense",
      "challenge"
    ]
  },
  {
    "icon": "PuzzlePieceIcon",
    "words": [
      "jeu",
      "game",
      "integration"
    ]
  },
  {
    "icon": "ClipboardDocumentCheckIcon",
    "words": [
      "valid",
      "approbation",
      "controle",
      "audit",
      "verification"
    ]
  },
  {
    "icon": "ClipboardDocumentListIcon",
    "words": [
      "formulaire",
      "liste",
      "inventaire",
      "checklist",
      "tache",
      "todo",
      "demande"
    ]
  },
  {
    "icon": "PencilSquareIcon",
    "words": [
      "saisie",
      "edition",
      "redaction"
    ]
  },
  {
    "icon": "TableCellsIcon",
    "words": [
      "grille",
      "feuille",
      "tableur",
      "spreadsheet"
    ]
  },
  {
    "icon": "LockClosedIcon",
    "words": [
      "confidentiel",
      "prive",
      "verrou"
    ]
  },
  {
    "icon": "KeyIcon",
    "words": [
      "cle",
      "licence",
      "acces",
      "secret"
    ]
  },
  {
    "icon": "MapPinIcon",
    "words": [
      "lieu",
      "adresse",
      "localisation",
      "site",
      "emplacement",
      "map",
      "location"
    ]
  },
  {
    "icon": "GlobeAltIcon",
    "words": [
      "international",
      "web",
      "langue",
      "monde"
    ]
  },
  {
    "icon": "IdentificationIcon",
    "words": [
      "badge",
      "kyc"
    ]
  },
  {
    "icon": "InboxIcon",
    "words": [
      "boite",
      "inbox",
      "reception"
    ]
  },
  {
    "icon": "LifebuoyIcon",
    "words": [
      "support",
      "assistance",
      "help",
      "depannage"
    ]
  },
  {
    "icon": "InformationCircleIcon",
    "words": [
      "information",
      "info",
      "apropos",
      "about"
    ]
  },
  {
    "icon": "QuestionMarkCircleIcon",
    "words": [
      "faq",
      "question"
    ]
  },
  {
    "icon": "WrenchIcon",
    "words": [
      "materiel",
      "equipement",
      "outil",
      "maintenance",
      "tool"
    ]
  },
  {
    "icon": "ComputerDesktopIcon",
    "words": [
      "ordinateur",
      "informatique",
      "poste",
      "systeme"
    ]
  },
  {
    "icon": "ServerIcon",
    "words": [
      "serveur",
      "infrastructure",
      "hebergement"
    ]
  },
  {
    "icon": "CircleStackIcon",
    "words": [
      "donnees",
      "data",
      "stockage"
    ]
  },
  {
    "icon": "CpuChipIcon",
    "words": [
      "algorithme",
      "automatisation"
    ]
  },
  {
    "icon": "HeartIcon",
    "words": [
      "sante",
      "health",
      "favoris",
      "soin"
    ]
  },
  {
    "icon": "ScaleIcon",
    "words": [
      "juridique",
      "legal",
      "balance",
      "comparaison",
      "droit",
      "contentieux"
    ]
  },
  {
    "icon": "TicketIcon",
    "words": [
      "billet",
      "ticket",
      "reservation"
    ]
  },
  {
    "icon": "GiftIcon",
    "words": [
      "cadeau",
      "don",
      "avantage",
      "bonus",
      "promotion"
    ]
  },
  {
    "icon": "CakeIcon",
    "words": [
      "anniversaire",
      "fete"
    ]
  },
  {
    "icon": "CameraIcon",
    "words": [
      "camera"
    ]
  },
  {
    "icon": "PhotoIcon",
    "words": [
      "image",
      "photo",
      "galerie",
      "media"
    ]
  },
  {
    "icon": "NewspaperIcon",
    "words": [
      "actualite",
      "news",
      "article",
      "blog",
      "presse"
    ]
  },
  {
    "icon": "PresentationChartLineIcon",
    "words": [
      "presentation",
      "reporting"
    ]
  },
  {
    "icon": "CurrencyDollarIcon",
    "words": [
      "dollar",
      "prix",
      "tarif",
      "cout",
      "price"
    ]
  },
  {
    "icon": "CurrencyEuroIcon",
    "words": [
      "euro"
    ]
  },
  {
    "icon": "ArrowsRightLeftIcon",
    "words": [
      "transfert",
      "import",
      "export",
      "flux",
      "virement",
      "mouvement"
    ]
  },
  {
    "icon": "BeakerIcon",
    "words": [
      "laboratoire",
      "test",
      "essai",
      "experience"
    ]
  },
  {
    "icon": "FaceSmileIcon",
    "words": [
      "satisfaction",
      "avis",
      "feedback"
    ]
  },
  {
    "icon": "HandRaisedIcon",
    "words": [
      "benevole",
      "volontaire",
      "participation"
    ]
  },
  {
    "icon": "DevicePhoneMobileIcon",
    "words": [
      "mobile",
      "application"
    ]
  },
  {
    "icon": "PhoneIcon",
    "words": [
      "telephone",
      "appel"
    ]
  },
  {
    "icon": "FireIcon",
    "words": [
      "urgent",
      "prioritaire"
    ]
  },
  {
    "icon": "BoltIcon",
    "words": [
      "energie",
      "rapide",
      "electricite"
    ]
  },
  {
    "icon": "MoonIcon",
    "words": [
      "nuit",
      "sombre"
    ]
  },
  {
    "icon": "CalculatorIcon",
    "words": [
      "calcul",
      "calculateur",
      "comptabilite",
      "compta"
    ]
  },
  {
    "icon": "UserCircleIcon",
    "words": [
      "compte",
      "espace"
    ]
  },
  {
    "icon": "TagIcon",
    "words": [
      "etiquette",
      "categorie",
      "tag",
      "marque"
    ]
  },
  {
    "icon": "StarIcon",
    "words": [
      "favori",
      "evaluation",
      "notation",
      "rating"
    ]
  },
  {
    "icon": "MagnifyingGlassIcon",
    "words": [
      "recherche",
      "search",
      "explorer",
      "trouver"
    ]
  },
  {
    "icon": "RectangleStackIcon",
    "words": [
      "collection",
      "lots",
      "stack"
    ]
  },
  {
    "icon": "SparklesIcon",
    "words": [
      "nouveau",
      "nouveaute",
      "assistant",
      "copilote"
    ]
  },
  {
    "icon": "CheckCircleIcon",
    "words": [
      "termine",
      "accompli",
      "done"
    ]
  },
  {
    "icon": "ArrowPathRoundedSquareIcon",
    "words": [
      "cycle",
      "renouvellement",
      "recurrent",
      "abonnement",
      "subscription",
      "workflow",
      "processus",
      "process"
    ]
  },
  {
    "icon": "ArrowTopRightOnSquareIcon",
    "words": [
      "externe",
      "lien",
      "link"
    ]
  },
  {
    "icon": "DocumentIcon",
    "words": [
      "fichier",
      "piece",
      "attachement"
    ]
  },
  {
    "icon": "TrashIcon",
    "words": [
      "corbeille",
      "suppression",
      "trash"
    ]
  }
];
